import { useState } from 'react'
import { apiFetch } from '../../lib/api.js'

const QUESTIONS = [
  { key: 'prefere',             label: 'Ce que tu as préféré',                          placeholder: 'Ce qui t\'a le plus plu…' },
  { key: 'amelioration',        label: 'Ce qui devrait être amélioré en priorité',       placeholder: 'Ce qui pourrait être mieux…' },
  { key: 'idee_fonctionnalite', label: 'Une idée de fonctionnalité supplémentaire',      placeholder: 'Une idée ou un souhait…' },
]

/**
 * Formulaire d'avis en 2 étapes (étoiles → texte libre).
 * Persistance serveur à chaque étape.
 *
 * Props :
 *   source       — 'joueur' | 'hote'
 *   code         — code de partie (joueur uniquement)
 *   session      — objet session (joueur) avec session.token
 *   partieId     — UUID partie (hôte uniquement)
 *   existing     — feedback existant en base { note?, prefere?, amelioration?, idee_fonctionnalite? }
 *   onDone       — callback appelé à la fin ou au "Passer"
 */
export default function FeedbackForm({ source, code, session, partieId, existing, onDone }) {
  const startStep = existing?.note ? 2 : 1
  const [step, setStep]           = useState(startStep)
  const [note, setNote]           = useState(existing?.note ?? 0)
  const [hoverNote, setHoverNote] = useState(0)
  const [texts, setTexts]         = useState({
    prefere:             existing?.prefere             ?? '',
    amelioration:        existing?.amelioration        ?? '',
    idee_fonctionnalite: existing?.idee_fonctionnalite ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [done, setDone]     = useState(false)

  function apiUrl() {
    return source === 'joueur' ? `/api/feedbacks/player/${code}` : '/api/feedbacks/host'
  }

  function apiOptions(body) {
    const opts = { method: 'POST', body: JSON.stringify(body) }
    if (source === 'joueur') opts.headers = { 'x-session-token': session?.token }
    return opts
  }

  function buildPayload(extra) {
    if (source === 'hote') return { partie_id: partieId, ...extra }
    return extra
  }

  async function saveStep1() {
    if (!note || saving) return
    setSaving(true)
    try {
      await apiFetch(apiUrl(), apiOptions(buildPayload({ note })))
      setStep(2)
    } catch (_) {}
    setSaving(false)
  }

  async function saveStep2() {
    setSaving(true)
    try {
      await apiFetch(apiUrl(), apiOptions(buildPayload({
        note,
        prefere:             texts.prefere             || null,
        amelioration:        texts.amelioration        || null,
        idee_fonctionnalite: texts.idee_fonctionnalite || null,
      })))
    } catch (_) {}
    setSaving(false)
    setDone(true)
    onDone?.()
  }

  if (done) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="text-5xl">🎉</div>
        <p className="text-white font-semibold">Merci pour ton avis !</p>
        <p className="text-white/50 text-sm">Ton retour nous aide à améliorer KartQuiz.</p>
      </div>
    )
  }

  const displayNote = hoverNote || note

  return (
    <div className="flex flex-col gap-5">
      {/* Indicateur d'étape */}
      <div className="flex items-center gap-2 justify-center">
        {[1, 2].map(n => (
          <div
            key={n}
            className={`h-1 rounded-full transition-all duration-300 ${
              n <= step ? 'w-8 bg-blue-400' : 'w-4 bg-white/20'
            }`}
          />
        ))}
      </div>

      {/* Étape 1 — Note */}
      {step === 1 && (
        <>
          <div className="text-center space-y-1">
            <p className="text-white font-semibold text-base">Quelle est ton expérience globale ?</p>
            <p className="text-white/50 text-xs">Clique sur une étoile pour noter</p>
          </div>

          <div
            className="flex justify-center gap-1"
            onMouseLeave={() => setHoverNote(0)}
          >
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                onClick={() => setNote(n)}
                onMouseEnter={() => setHoverNote(n)}
                className="text-4xl transition-transform duration-100 hover:scale-125 active:scale-95 select-none"
                aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
              >
                {n <= displayNote ? '⭐' : '☆'}
              </button>
            ))}
          </div>

          {note > 0 && (
            <p className="text-center text-white/50 text-xs">
              {['', 'Très insatisfaisant', 'Insatisfaisant', 'Correct', 'Bien', 'Excellent !'][note]}
            </p>
          )}

          <button
            onClick={saveStep1}
            disabled={!note || saving}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-semibold py-3 rounded-xl transition text-sm"
          >
            {saving ? '…' : 'Suivant →'}
          </button>
        </>
      )}

      {/* Étape 2 — Textes libres */}
      {step === 2 && (
        <>
          <div className="text-center space-y-1">
            <p className="text-white font-semibold text-base">Aidez-nous à progresser</p>
            <p className="text-white/50 text-xs">Tous les champs sont facultatifs</p>
          </div>

          <div className="flex flex-col gap-3">
            {QUESTIONS.map(({ key, label, placeholder }) => (
              <div key={key} className="flex flex-col gap-1">
                <label className="text-white/60 text-xs font-medium">{label}</label>
                <textarea
                  value={texts[key]}
                  onChange={e => setTexts(t => ({ ...t, [key]: e.target.value }))}
                  rows={2}
                  placeholder={placeholder}
                  className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-blue-400 placeholder:text-white/30 transition"
                />
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onDone?.()}
              className="flex-1 bg-white/10 hover:bg-white/20 text-white/60 hover:text-white font-medium py-3 rounded-xl transition text-sm"
            >
              Passer
            </button>
            <button
              onClick={saveStep2}
              disabled={saving}
              className="flex-[2] bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-semibold py-3 rounded-xl transition text-sm"
            >
              {saving ? '…' : 'Envoyer mon avis'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
